import type { XSSDetails } from "@/Util/utils";
import { addDays, addSeconds } from "date-fns";
import type { ComponentType, SvelteComponent } from "svelte";
import type { Writable } from "svelte/store";
import type { AchievabilityStatus, ProgramAchievability } from "@/Program/modules/ProgramAchievability";
import type { WorkoutPlayerType } from "@/Util/api";
import type { Exercise } from "./ActivityFunctions";

export const xssColors = {
    xlss: '#0B5DBC',
    xhss: '#ffc300',
    xpss: '#e03f42',
    deviationGood: '#7ED321',
    deviationWarn: '#ffaa00',
    deviationAlert: '#d02224',
} as const

export enum FocusTypes {
    RoadSprinter = "Road Sprinter",
    Pursuiter = "Pursuiter",
    Puncheur = "Puncheur",
    BreakawaySpecialist = "Breakaway Specialist",
    Rouleur = "Rouleur",
    GcSpecialist = "GC Specialist",
    Climber = "Climber",
    SprintTimeTriallist = "Sprint Time-Triallist",
    LongSprinter = "Long Sprinter",
    LongDistance = "Long Distance",
    ShortMiddleDistance = "Short Middle Distance",
    MiddleDistance = "Middle Distance",
    Endurance = "Endurance",
}

export type ProgramFocusTypes = Exclude<FocusTypes, FocusTypes.Endurance>
export const programFocusTypes = Object.values(FocusTypes).filter((f) : f is ProgramFocusTypes => f !== FocusTypes.Endurance)

export type FocusDescription = {
    description: string,
    minute_power: number | null,
    endurance_alt_description?: string,
    running?: boolean,
}

export enum SpecificityTypes {
    Polar = "Polar",
    Mixed = "Mixed",
    Pure = "Pure",   
}

export const specificityValues = {
    [SpecificityTypes.Pure]: 0.75,
    [SpecificityTypes.Mixed]: 0.5,
    [SpecificityTypes.Polar]: 0.25, 
}

export const specificityThreshold = {
    [SpecificityTypes.Pure]: "Less Threshold",
    [SpecificityTypes.Mixed]: "Some Threshold",
    [SpecificityTypes.Polar]: "More Threshold", 
}

export type MinutePower = {
    x: number,
    dur: string,
    userp: number,
}

export type SpecificityDescription = {
    description: string,
}

export type DayIntensity = 'advisor' | 'low' | 'high'

export type Availability = {
    availability: boolean,
    time: string,
    duration: number,
    fixed_availability?: boolean,
    intensity?: DayIntensity, // Missing means 'advisor'. Only meaningful when availability is true.
    venue?: string, // TODO: These two should be removed, probably
    training_category?: string,
}
export type AvailabilitySettings = Availability & {
    unset: boolean,
    revert: boolean,
    saveAsDefault: boolean,
}
export type SelectedMode = "target_focus" | "target_xss" | "target_race" | "none"

export type Polyline = [number, number][]
export interface ActivityRecordsData  {
    hr: number[],
    cad: number[],
    spd: number[],
    tgt: number[],
    dist: number[],
    lat: number[],
    lng: number[],
    alt: number[],
    power: number[],
    time: number[],
    polyline: Polyline
}

export interface RaceData extends ActivityRecordsData {
    originalTime: number[],
    originalPower: number[],
}

export type RaceRecords = {
    original_power: Array<number | null>,
    polyline: Polyline,
    race_data: RaceData,
    source_activity_path: string,
}

export type Signature = {
    pp: number,
    atc: number,
    ftp: number,
    ltp: number,
}

export enum ForecastType {
    Rest,
    Recovery,
    HighIntensity,
    LowIntensity,
    Event,
}
export type ForecastActivityState = {
    type: ForecastType,
    specificity: SpecificityTypes,
    focus: FocusTypes,
    nextDayType: ForecastType | null,
    daysSinceBT: number,
    weeksToEvent: number,
    daysToHighIntensity: number,
    polarizationLevel: number,
    isChallenging: boolean,
    currentSignature: Signature,
    activitySignature: Signature,
    targets: XSSDetails,
    duration: number,
    path: string,
}

export enum ProgramType {
    Goal = "goal", 
    Event = "event", 
    Race = "race", 
    BaseBuildPeak = "targetEvent",
    Continuous  = "continuous",
    Challenge = "challenge"
}

export type XfaiPrograms = ProgramType.Event | ProgramType.Goal | ProgramType.Race

export const xfaiProgramTypes = [ProgramType.Goal, ProgramType.Event, ProgramType.Race]

export interface AthleteInfo {
    db_name: string,
    default?: boolean,
    desc: string,
    duration: number,
    name: string,
    per_weight: boolean,
    workoutname: string,
}

export type Athletes =  Record<FocusTypes, AthleteInfo>

export type XataFormProps<T> = {
    plan?: Writable<T>,
    class?: string,
    achievabilityId?: string,
    achievability: AchievabilityStatus,
}
export type XataFormComponent = ComponentType<SvelteComponent<XataFormProps<any>>>

export type ForecastedTargets = {
    signature: XSSDetails | null,
    trainingLoads: XSSDetails | null,
    focusPower: number | null,
    xss: XSSDetails | null,
}

export type XfaiFormProps<T extends XfaiPrograms> = {
    plan: Writable<ProgramPlanMap[T]>,
    class?: string,
    forecastInputs: any,
    disabled: boolean,
    programAchievability: ProgramAchievability,
    achievabilityId?: string,
    forecastReady?: boolean,
    refresh: () => void,
}
export type XfaiFormComponent<T extends XfaiPrograms> = ComponentType<SvelteComponent<XfaiFormProps<T>>>

export enum ImprovementRate {
    OffSeason = -2,
    Taper = -1,
    Maintenance = 0,
    Slow = 1,
    Moderate1 = 2,
    Moderate2 = 3,
    Aggressive1 = 4,
    Aggressive2 = 5,
    Extreme1 = 6,
    Extreme2 = 7,
}

export const ImprovementRateText: Record<ImprovementRate, String> = {
    [ImprovementRate.OffSeason]: "Off-Season",
    [ImprovementRate.Taper]: "Ease Off Period",
    [ImprovementRate.Maintenance]: "Maintenance",
    [ImprovementRate.Slow]: "Slow",
    [ImprovementRate.Moderate1]: "Moderate-1",
    [ImprovementRate.Moderate2]: "Moderate-2",
    [ImprovementRate.Aggressive1]: "Aggressive-1",
    [ImprovementRate.Aggressive2]: "Aggressive-2",
    [ImprovementRate.Extreme1]: "Extreme-1",
    [ImprovementRate.Extreme2]: "Extreme-2",
}

export type FaIconTypes = "fa-thin" | "fa-regular" | "fa-solid" | "fa-light"
/**
    300: Server Error
    | 301: Couldn't create taper
    | 302: General failure (likely code)
    | 303: Constraints failed
    | 201: Little flexibility (Event)
    | 202: Exceeded max weekly hours
    | 203: Different final power (Goal)
    | 204: Adjusted training loads (Goal)
    | 100: Success and achievable
 */
type ForecastResultCodes = 100 | 300 | 301 | 302 | 303 | 201 | 202 | 203 | 204 | undefined
interface CodeStatusInfo extends Record<ForecastResultCodes, {status: AchievabilityStatus}> {
    100: {status: "fine"},
    300: {status: "error"},
    301: {status: "error"},
    302: {status: "error"},
    303: {status: "error"},
    201: {status: "warning"},
    202: {status: "warning", weeklyHours: number},
    203: {status: "warning", finalPower: number},
    204: {status: "warning"},
}
export type ForecastInfo = {
    [K in ForecastResultCodes]: {
        resultCode: K,
        done:boolean, 
        results: any
        running: boolean
    } & (K extends keyof CodeStatusInfo ? CodeStatusInfo[K] : {})
}[ForecastResultCodes]

export const defaultForecastInfo: ForecastInfo = {
    results: null,
    status: "fine",
    resultCode: undefined,
    done: false,
    running: false,
}

const _getFocus = (athletes, sfd) => {
    const _athletes = Object.values<any>(athletes)
    if (_athletes.length < 1) return '';
    let result = _athletes[_athletes.length - 1];
    
    if (sfd > 0) {
        const count = _athletes.length;
        for (let i = 0; i < count; i++) {
            const athlete = _athletes[i];
            const next_athlete = i < count-1 ? _athletes[i+1] : null;
            
            if (next_athlete) {
                const ath_duration = athlete.duration;
                const next_ath_duration = next_athlete.duration;
                
                if (sfd < (ath_duration + next_ath_duration) / 2) {
                    result = athlete;
                    break;
                }
            }
        }
    }
    return result;
}

export const getFocus = (athletes, sfd) => {
    return _getFocus(athletes, sfd).name
}

export const getFocusWorkoutName = (athletes, sfd) => {
    return _getFocus(athletes, sfd).workoutname
}

export const getFocusDuration = (athletes, focus) => {
    for (const ath in athletes) {
        if (athletes[ath].workoutname == focus) {
            return athletes[ath].duration
        }
    }
    return 0;
}

export const defaultForecastOptions = {
    kMedian: 0.01,

    optimizeFocusPower: true,
    // 'optimizeFocusPower': false,
    kFocusPower: 1000,

    optimizePreEventForm: true, // Enable so that form is positive on day of event. Regular kFormRatio penalty doesn't apply to event as it does to the other days.
    optimizePostEventForm: true, // Enable so that form recovers 2 days after event.
    kEventForm: 200,

    kXss: 0.00004,

    kFormRatio: 100,
    lowRestDays: 2,
    lowRestForm: -10,
    highRestDays: 4,
    highRestForm: 0,
    eventRestDays: 2,

    periodization: 0.025,
    recovery: 0.0,
    maxWeeklyHours: 8,

    difficulty: 100.0,
    difficultyFromTrainingLoad: false,

    minimizeTraining: true,
};

export const defaultTrainingPlanSettings = {
    taper: 2,
    polarization: 4,
    periodization: 0.025,
    recovery: 0.2,
    maxWeeklyHours: 8,
    difficulty: 100.0,
    difficultyFromTrainingLoad: false,
    minimizeTraining: true,
}

export type TrainingPlanSettings = typeof defaultTrainingPlanSettings

export const defaultUnsetAvailability: Availability = {
    availability: true,
    duration: -1,
    time: 'advisor',
    intensity: 'advisor',
}

export const toggledRestActivityBody = (startDate: Date) => {
    return {
        duration: 1,
        title: 'Rest',
        start_date: startDate.toISOString(),
        end_date: addSeconds(startDate, 60).toISOString(),
        xss: 0,
        xlss: 0,
        xhss: 0,
        xpss: 0,
        type: 'None',
        id: '',
        forUser: '',
        from_xata: 0,
        options: {},
    }
}

export const ReccTrainingPills = [
    { label: 'All', value: 0, exerciseType: 'All', activityType: '', },
    { label: 'Outdoor', value: 1, exerciseType: 'Activity', activityType: 'outdoor' },
    { label: 'Virtual', value: 2, exerciseType: 'Activity', activityType: 'virtual' },
    { label: 'Sessions', value: 3, exerciseType: 'Session', activityType: '' },
    { label: 'Workouts', value: 4, exerciseType: 'Workout', activityType: '' },
]

export const cont_n_weeks = 6
export const challenge_n_days = 30
export const maxWeeklyHoursBounds = {
    max: 28,
    min: 4,
    step: 0.5,
}
// Max Weekly Hours that never limits a forecast: the hours in a week. Used to find how many hours a plan really needs.
export const unlimitedWeeklyHours = 168
export const eventReadinessBounds = {
    max: 5,
    min: 1,
    step: 0.1,
}
export const programDiffcultyBounds = {
    max: 150,
    min: 60,
    step: 1,
}

// Mirrors getStrainRating() in app/Http/helpers.php -- keep these buckets in sync with that
// function if the difficulty scale ever changes.
export function strainRatingText(difficulty: number): string {
    if (difficulty <= 45) return "Easy";
    if (difficulty <= 50) return "Easy";
    if (difficulty <= 62) return "Moderate";
    if (difficulty <= 75) return "Moderate";
    if (difficulty <= 90) return "Difficult";
    if (difficulty <= 110) return "Difficult";
    if (difficulty <= 130) return "Tough";
    if (difficulty <= 150) return "Tough";
    return "Hard";
}

export function strainRatingDiamonds(difficulty: number): string {
    if (difficulty <= 45) return "◆";
    if (difficulty <= 50) return "◆⬖";
    if (difficulty <= 62) return "◆◆";
    if (difficulty <= 75) return "◆◆⬖";
    if (difficulty <= 90) return "◆◆◆";
    if (difficulty <= 110) return "◆◆◆⬖";
    if (difficulty <= 130) return "◆◆◆◆";
    if (difficulty <= 150) return "◆◆◆◆⬖";
    return "◆◆◆◆◆";
}
export const periodizationBounds = {
    max: 0.05,
    min: 0,
    step: 0.005,
}
export const polarizationBounds = {
    max: 5,
    min: 1,
    step: 1,
}
export const polarizationValues = {
    1: {
        ratio: '1:1',
        percent: '50/50'
    },
    2: {
        ratio: '2:1',
        percent: '66/33'
    },
    3: {
        ratio: '3:1',
        percent: '75/25'
    },
    4: {
        ratio: '4:1',
        percent: '80/20'
    },
    5: {
        ratio: '5:1',
        percent: '83/17'
    },
}
export const recoveryBounds ={
    max: 1.2,
    min: -0.8,
    step: 0.1,
}

export type ShortWeekDayProperties = "Sun" | "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat"
export type CalendarDefaults  = Record<ShortWeekDayProperties, Availability>

export type OverrideAvailabilities = { [index: string]: Availability }
export type MovingTimeOption = "avg" | "mov" | "pedal"
export type DurabilityMetricOption = "total_work" | "above_threshold" | "carbs_used"
export type CalendarSettings = {
    defaults: CalendarDefaults
    availabilities: OverrideAvailabilities
    start_of_week: number
    hide_other_profiles: boolean
    use_aggregate_card: boolean
    use_moving_time: MovingTimeOption
    crop_activities: boolean,
    durability_metric: DurabilityMetricOption,
}

const defaultAvailabilityDay: Availability = {
    availability: true,
    time: 'advisor',
    duration: -1,
    intensity: 'advisor',
}

export const defaultCalSettingsForPlans: CalendarSettings = {
    defaults: {
        Sun: { ...defaultAvailabilityDay },
        Mon: { ...defaultAvailabilityDay },
        Tue: { ...defaultAvailabilityDay },
        Wed: { ...defaultAvailabilityDay },
        Thu: { ...defaultAvailabilityDay },
        Fri: { ...defaultAvailabilityDay },
        Sat: { ...defaultAvailabilityDay },
    },
    availabilities: {},
    start_of_week: 0,
    hide_other_profiles: true,
    use_aggregate_card: false,
    use_moving_time: "avg",
    crop_activities: false,
    durability_metric: "total_work"
}

export const durationSelections = [
    { label: '30 Mins',          value: 30 },
    { label: '45 Mins',          value: 45 },
    { label: '1 Hour',           value: 60 },
    { label: '1 Hour 15 Mins',   value: 75 },
    { label: '1 Hour 30 Mins',   value: 90 },
    { label: '1 Hour 45 Mins',   value: 105},
    { label: '2 Hours',          value: 120},
    { label: '2 Hours 15 Mins',  value: 135},
    { label: '2 Hours 30 Mins',  value: 150},
    { label: '2 Hours 45 Mins',  value: 165},
    { label: '3 Hours',          value: 180},
    { label: '3 Hours 15 Mins',  value: 195},
    { label: '3 Hours 30 Mins',  value: 210},
    { label: '3 Hours 45 Mins',  value: 225},
    { label: '4 Hours',          value: 240},
    { label: '5 Hours', value: 5*60},
    { label: '6 Hours', value: 6*60},
    { label: '7 Hours', value: 7*60},
];

export type TabNames = "calendar" | "dashboard" | "program" | "advisor" | "progression"

/* Program Page Default Plans and Types */

export type GoalTargetsMode = "focus" | "manual"
export type EventTargetsMode = "event" | "manual"

export const defaultGoalPlan = {
    program: ProgramType.Goal as ProgramType.Goal,
    targetDate: addDays(new Date(), 30),
    startDate: null as Date | null, // null = start today. A future date starts the plan then, with a Pre-Plan Maintenance Program until it.
    specificity: SpecificityTypes.Polar,
    focusPower: 1,
    targetAthlete: FocusTypes.SprintTimeTriallist as ProgramFocusTypes,
    targetsMode: "focus" as GoalTargetsMode,
    manualSignature: null as {ftp: number, hie: number, pp: number} | null, // ftp/pp in W, hie in J
    cal_settings: defaultCalSettingsForPlans,
    settings: {...defaultTrainingPlanSettings},
}
export type GoalPlan = typeof defaultGoalPlan

export const defaultEventPlan = {
    program: ProgramType.Event as ProgramType.Event,
    targetDate: new Date(),
    startDate: null as Date | null,
    specificity: SpecificityTypes.Mixed,
    xss: 0,
    targetAthlete: FocusTypes.SprintTimeTriallist as ProgramFocusTypes,
    name: "Event",
    type: "Road Race",
    terrain: "Flat",
    duration: 60,
    targetsMode: "event" as EventTargetsMode,
    manualXss: null as {xlss: number, xhss: number, xpss: number} | null,
    cal_settings: defaultCalSettingsForPlans,
    settings: {...defaultTrainingPlanSettings},
}
export type EventPlan = typeof defaultEventPlan


export type RacePlan = {
    program: ProgramType.Race,
    targetDate: Date,
    startDate: Date | null,
    specificity: SpecificityTypes,
    xss: number,
    targetAthlete: ProgramFocusTypes,
    name: string
    activityPath: string | null,
    fileData: FormData | null,
    raceRecords: RaceData | null,
    raceLogoUrl: string | null,
    raceSpeedScale: number,
    racePowerScale: number,
    userTargetWeight: number,
    riderWeight: number,
    carbsPerHour: number,
    ignoreCarbs: boolean,
    ignoreDifficulty:boolean,
    difficultyScore: number,
    cal_settings: CalendarSettings,
    settings: TrainingPlanSettings,
}

export const defaultRacePlan: RacePlan = {
    program: ProgramType.Race,
    targetDate: new Date(),
    startDate: null,
    specificity: SpecificityTypes.Mixed,
    xss: 0,
    targetAthlete: FocusTypes.SprintTimeTriallist as ProgramFocusTypes,
    name: "Event",
    activityPath: null,
    fileData: null,
    raceRecords: null,
    raceLogoUrl: null,
    raceSpeedScale: 1.0,
    racePowerScale: 1.0,
    userTargetWeight: 0,
    riderWeight: 0,
    carbsPerHour: 150,
    ignoreCarbs: true,
    ignoreDifficulty: true,
    difficultyScore: 200,
    cal_settings: defaultCalSettingsForPlans,
    settings: {...defaultTrainingPlanSettings},
}

export const defaultBbpPlan = {
    targetDate: addDays(new Date(), 120),
    specificity: SpecificityTypes.Mixed,
    targetAthlete: FocusTypes.SprintTimeTriallist as ProgramFocusTypes,
    irValue: 0,
    cal_settings: defaultCalSettingsForPlans,
    settings: {...defaultTrainingPlanSettings}
}
export type BbpPlan = typeof defaultBbpPlan

export const defaultContinuousPlan = {
    specificity: SpecificityTypes.Mixed,
    targetAthlete: FocusTypes.SprintTimeTriallist as ProgramFocusTypes,
    irValue: 0,
    cal_settings: defaultCalSettingsForPlans,
    settings: {...defaultTrainingPlanSettings}
}
export type ContinuousPlan = typeof defaultContinuousPlan

export const defaultChallengePlan =  {
    targetDate: addDays(new Date(), 30),
    specificity: SpecificityTypes.Mixed,
    targetAthlete: FocusTypes.SprintTimeTriallist as ProgramFocusTypes,
    irValue: 0,
    cal_settings: defaultCalSettingsForPlans,
    settings: {...defaultTrainingPlanSettings}
}
export type ChallengePlan = typeof defaultChallengePlan

export type AllXataPlans = BbpPlan | ContinuousPlan | ChallengePlan
export type AllXfaiPlans = GoalPlan | EventPlan | RacePlan
export type AllTrainingPlans = AllXataPlans | AllXfaiPlans

export interface ProgramPlanMap {
    [ProgramType.Goal] : GoalPlan,
    [ProgramType.Event] : EventPlan,
    [ProgramType.Race] : RacePlan,
    [ProgramType.Continuous] : ContinuousPlan,
    [ProgramType.BaseBuildPeak] : BbpPlan,
    [ProgramType.Challenge] : ChallengePlan,
}

export const emptyForecastedTargets: ForecastedTargets = {
    signature: null,
    trainingLoads: null,
    focusPower: null,
    xss: null,
}

export type TerrainTypes = "Flat" | "Hilly" | "Mountainous"
type TerrainInfo = Record<TerrainTypes, {highest_event_xssr: number, lowest_event_xssr: number, max_duration: number, specificity: SpecificityTypes, focus: ProgramFocusTypes}>
type EventOptions = Record<string, {terrains: Partial<TerrainInfo>, disabled: boolean}>
export const eventOptions: EventOptions = {
    "Road Race": {
        terrains: {
            "Flat": {
                highest_event_xssr: 90,
                lowest_event_xssr: 50,
                max_duration: 6,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.BreakawaySpecialist
            },
            "Hilly": {
                highest_event_xssr: 100,
                lowest_event_xssr: 50,
                max_duration: 6,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.Puncheur
            },
            "Mountainous": {
                highest_event_xssr: 90,
                lowest_event_xssr: 50,
                max_duration: 6,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.Climber
            }
        },
        disabled: false,
    },
    "Criterium": {
        terrains: {
            "Flat": {
                highest_event_xssr: 120,
                lowest_event_xssr: 75,
                max_duration: 3,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.RoadSprinter
            },
            "Hilly": {
                highest_event_xssr: 120,
                lowest_event_xssr: 75,
                max_duration: 3,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.Pursuiter
            },
        },
        disabled: false,
    },
    "Gravel Race": {
        terrains: {
            "Flat": {
                highest_event_xssr: 80,
                lowest_event_xssr: 40,
                max_duration: 12,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.Rouleur
            },
            "Hilly": {
                highest_event_xssr: 80,
                lowest_event_xssr: 40,
                max_duration: 12,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.BreakawaySpecialist
            },
            "Mountainous": {
                highest_event_xssr: 80,
                lowest_event_xssr: 40,
                max_duration: 12,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.GcSpecialist
            }
        },
        disabled: false,
    },
    "Gravel Recreational": {
        terrains: {
            "Flat": {
                highest_event_xssr: 50,
                lowest_event_xssr: 25,
                max_duration: 12,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.GcSpecialist
            },
            "Hilly": {
                highest_event_xssr: 50,
                lowest_event_xssr: 25,
                max_duration: 12,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.Rouleur
            },
            "Mountainous": {
                highest_event_xssr: 50,
                lowest_event_xssr: 25,
                max_duration: 12,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.Climber
            }
        },
        disabled: false,
    },
    "Track-Sprints": {
        terrains: {
            "Flat": {
                highest_event_xssr: 140,
                lowest_event_xssr: 100,
                max_duration: 2,
                specificity: SpecificityTypes.Pure,
                focus: FocusTypes.RoadSprinter
            },
        },
        disabled: true,
    },
    "Track-Points/Elimination": {
        terrains: {
            "Flat": {
                highest_event_xssr: 140,
                lowest_event_xssr: 100,
                max_duration: 2,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.RoadSprinter
            },
        },
        disabled: false,
    },
    "Track-Scratch": {
        terrains: {
            "Flat": {
                highest_event_xssr: 140,
                lowest_event_xssr: 100,
                max_duration: 2,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.Pursuiter
            },
        },
        disabled: false,
    },
    "Track-Pursuit": {
        terrains: {
            "Flat": {
                highest_event_xssr: 140,
                lowest_event_xssr: 100,
                max_duration: 2,
                specificity: SpecificityTypes.Pure,
                focus: FocusTypes.Pursuiter
            },
        },
        disabled: true,
    },
    "Competitive Gran Fondo": {
        terrains: {
            "Flat": {
                highest_event_xssr: 80,
                lowest_event_xssr: 40,
                max_duration: 8,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.BreakawaySpecialist
            },
            "Hilly": {
                highest_event_xssr: 80,
                lowest_event_xssr: 40,
                max_duration: 8,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.Rouleur
            },
            "Mountainous": {
                highest_event_xssr: 80,
                lowest_event_xssr: 40,
                max_duration: 8,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.Climber
            }
        },
        disabled: false,
    },
    "Recreational Gran Fondo": {
        terrains: {
            "Flat": {
                highest_event_xssr: 55,
                lowest_event_xssr: 27.5,
                max_duration: 8,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.Rouleur
            },
            "Hilly": {
                highest_event_xssr: 55,
                lowest_event_xssr: 27.5,
                max_duration: 8,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.GcSpecialist
            },
            "Mountainous": {
                highest_event_xssr: 55,
                lowest_event_xssr: 27.5,
                max_duration: 8,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.Climber
            }
        },
        disabled: false,
    },
    "15k Time Trial": {
        terrains: {
            "Flat": {
                highest_event_xssr: 100,
                lowest_event_xssr: 90,
                max_duration: 2,
                specificity: SpecificityTypes.Pure,
                focus: FocusTypes.SprintTimeTriallist
            },
            "Hilly": {
                highest_event_xssr: 100,
                lowest_event_xssr: 90,
                max_duration: 2,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.Rouleur
            },
            "Mountainous": {
                highest_event_xssr: 0,
                lowest_event_xssr: 90,
                max_duration: 2,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.Climber
            }
        },
        disabled: true,
    },
    "5 Minute Hill Climb": {
        terrains: {
            "Flat": {
                highest_event_xssr: 200,
                lowest_event_xssr: 200,
                max_duration: 1,
                specificity: SpecificityTypes.Pure,
                focus: FocusTypes.BreakawaySpecialist
            },
        },
        disabled: true,
    },
    "8 Minute Hill Climb": {
        terrains: {
            "Flat": {
                highest_event_xssr: 200,
                lowest_event_xssr: 200,
                max_duration: 1,
                specificity: SpecificityTypes.Pure,
                focus: FocusTypes.GcSpecialist
            },
        },
        disabled: true,
    },
    "10 Minute Hill Climb": {
        terrains: {
            "Flat": {
                highest_event_xssr: 200,
                lowest_event_xssr: 200,
                max_duration: 1,
                specificity: SpecificityTypes.Pure,
                focus: FocusTypes.Climber
            },
        },
        disabled: true,
    },
    "MTB-XC": {
        terrains: {
            "Hilly": {
                highest_event_xssr: 70,
                lowest_event_xssr: 50,
                max_duration: 6,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.GcSpecialist
            },
            "Mountainous": {
                highest_event_xssr: 60,
                lowest_event_xssr: 40,
                max_duration: 6,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.GcSpecialist
            }
        },
        disabled: false,
    },
    "MTB-XCM": {
        terrains: {
            "Hilly": {
                highest_event_xssr: 70,
                lowest_event_xssr: 35,
                max_duration: 12,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.Climber
            },
            "Mountainous": {
                highest_event_xssr: 60,
                lowest_event_xssr: 30,
                max_duration: 12,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.Climber
            }
        },
        disabled: false,
    },
    "Cyclocross": {
        terrains: {
            "Hilly": {
                highest_event_xssr: 140,
                lowest_event_xssr: 110,
                max_duration: 2,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.Puncheur
            },
        },
        disabled: false,
    },
    "Competitive Group Ride": {
        terrains: {
            "Flat": {
                highest_event_xssr: 80,
                lowest_event_xssr: 40,
                max_duration: 6,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.RoadSprinter
            },
            "Hilly": {
                highest_event_xssr: 80,
                lowest_event_xssr: 40,
                max_duration: 6,
                specificity: SpecificityTypes.Mixed,
                focus: FocusTypes.Puncheur
            },
            "Mountainous": {
                highest_event_xssr: 70,
                lowest_event_xssr: 35,
                max_duration: 6,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.Climber
            }
        },
        disabled: false,
    },
    "Friendly Group Ride": {
        terrains: {
            "Flat": {
                highest_event_xssr: 60,
                lowest_event_xssr: 30,
                max_duration: 6,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.Rouleur
            },
            "Hilly": {
                highest_event_xssr: 60,
                lowest_event_xssr: 30,
                max_duration: 6,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.GcSpecialist
            },
            "Mountainous": {
                highest_event_xssr: 60,
                lowest_event_xssr: 30,
                max_duration: 6,
                specificity: SpecificityTypes.Polar,
                focus: FocusTypes.Climber
            }
        },
        disabled: false,
    },
}

export interface AddMarkerInterface {
    id: string;
    lat: number;
    lng: number;
    image: string;
    label?: string;
    group?: string;
    hidden?: boolean;
    borderColor?: string;
    size?: {
        width: number;
        height: number;
    }
}
export type MapController = {
    removeMarker: (id: string, group?: string) => void,
    hideMarker: (id: string, group?: string) => void,
    showMarker: (id: string, group?: string) => void,
    hideMarkersInGroup: (group: string) => void,
    showMarkersInGroup: (group: string) => void,
    addMarker: (markerInterface: AddMarkerInterface) => void,
    setMarkerPosition: (id: string, lat: number, lng: number, group?: string) => void,
    resetZoom: () => void,
    setStartMarkerPosition: (lat: number, lng: number) => void,
    setEndMarkerPosition: (lat: number, lng: number) => void,
    resetStartMarker: () => void,
    resetEndMarker: () => void,
    setCenterFromData: (startIndex: number, endIndex: number, data: {lat: number, lng: number}[]) => void,
    updateMarkerLabel: (id: string, label: string, group?: string) => void,
    drawSectionLine: (path: {lat: number, lng: number}[]) => void,
    removeSectionLine: () => void,
    noop?: boolean,
}
export const noopMapController: MapController = {
    removeMarker: () => {},
    hideMarker: () => {},
    showMarker: () => {},
    addMarker: () => {},
    hideMarkersInGroup: () => {},
    showMarkersInGroup: () => {},
    setMarkerPosition: () => {},
    resetZoom: () => {},
    setStartMarkerPosition: () => {},
    setEndMarkerPosition: () => {},
    resetStartMarker: () => {},
    resetEndMarker: () => {},
    setCenterFromData: () => {},
    updateMarkerLabel: () => {},
    drawSectionLine: () => {},
    removeSectionLine: () => {},
    noop: true,
};
export interface MapControllerInterface {
    controller: MapController,
    group?: string
}
export const defaultMapControllerInterface: MapControllerInterface = {
    controller: noopMapController,
    group: 'default',
}

export type EventTypes = keyof typeof eventOptions

const workoutNames: Array<Array<String>> = [
    ["Power Sprinter", "Power Sprinter"],
    ["Road Sprinter", "Road Sprinter"],
    ["Pursuiter", "Pursuiter"],
    ["Puncheur", "Puncheur"],
    ["Breakaway Specialist", "Breakaway Specialist"],
    ["Rouleur", "Rouleur"],
    ["GC Specialist", "GC Specialist"],
    ["Climber", "Climber"],
    ["Sprint Time-Triallist", "Endurance"],
    ["Time-Triallist", "Endurance"],
    ["Century Rider", "Endurance"],
    ["Triathlete", "Endurance"],
]

//@ts-ignore
export const workoutNameMap: Map<string, string> = new Map(workoutNames)

export const programTypeTitle = {
    [ProgramType.Challenge]: 'Challenge',
    [ProgramType.Goal]: 'Goal',
    [ProgramType.Race]: 'Race with Xert Relative Power',
    [ProgramType.Event]: 'Event',
    [ProgramType.BaseBuildPeak]: 'Base-Build-Peak',
    [ProgramType.Continuous]: 'Continuous',
}
export const shortProgramTypeTitle = {
    ...programTypeTitle,
    [ProgramType.Race]: 'Race',
}

export const programTypeDescription = {
    [ProgramType.Challenge]: 'A 30-day Program that encourage you to reach new levels within a 30 day period.',
    [ProgramType.Goal]: 'Generate a training plan the enables you to reach a power personal best.',
    [ProgramType.Race]: 'Bringing the most advanced power training tools together into your training plan.',
    [ProgramType.Event]: 'Generate a training plan that prepares you for an event.',
    [ProgramType.BaseBuildPeak]: 'A generalized 120-day program with a Base phase of 45 days, Build phase of 45 days and a Peak phase of 30 days.',
    [ProgramType.Continuous]: 'The default program offered by the Adaptive Training Advisor.  Your goals and training are on a continuous improvement path and can be reviewed on a rolling 6 week training window.',
}

export const programTypeIcon = {
    [ProgramType.Challenge]: 'fa-tachometer',
    [ProgramType.Goal]: 'fa-bullseye',
    [ProgramType.Race]: 'fa-flag-checkered',
    [ProgramType.Event]: 'fa-trophy',
    [ProgramType.BaseBuildPeak]: 'fa-calendar',
    [ProgramType.Continuous]: 'fa-infinity',
}

export type xataTableRow = {
    title: string,
    starting: number,
    current: number,
    projected: number,
}

export const max_tl_week = {
    low: 2.2,
    high: 0.33,
    peak: 0.165,
}

// enum starts from Monday=0
export enum daysOfWeek {
    Sunday,
    Monday,
    Tuesday,
    Wednesday,
    Thursday,
    Friday,
    Saturday,
}
export const daysOfWeekStrings: [daysOfWeek, string, ShortWeekDayProperties][] = [
    [daysOfWeek.Monday, 'Monday', 'Mon'],
    [daysOfWeek.Tuesday, 'Tuesday', 'Tue'],
    [daysOfWeek.Wednesday, 'Wednesday', 'Wed'],
    [daysOfWeek.Thursday, 'Thursday', 'Thu'],
    [daysOfWeek.Friday, 'Friday', 'Fri'],
    [daysOfWeek.Saturday, 'Saturday', 'Sat'],
    [daysOfWeek.Sunday, 'Sunday', 'Sun'] 
]

export const defaultActivityChartOptions = {
    showHeartRate: false,
    showTargetPower: false,
}

export const improvementRateDescriptions: Record<ImprovementRate, String> = {
    [ImprovementRate.OffSeason]: "The Off-Season Improvement Rate can be used during times when you are intentionally looking to decrease your Training Load in preparation for an increase in training in the future. Switching to Off-Season can help you recover from a long period of training and give your mind and body a rest before getting into a more intense training regimen.",
    [ImprovementRate.Taper]: "This is the rate to choose once you’ve reached your goal and would like to maintain your fitness a week before your event and ensure you are appropriately recovered.",
    [ImprovementRate.Maintenance]: "This is the rate to choose once you’ve reached your goal and would like to maintain that level of fitness and performance.",
    [ImprovementRate.Slow]: "Generally used for blocks where you’re wanting to ease into your training period without a large time investment.",
    [ImprovementRate.Moderate1]: "This choice enables a balance of higher intensity and volume with consistent recovery, enabling this improvement rate to be sustainable over longer blocks of training.",
    [ImprovementRate.Moderate2]: "This choice enables a balance of higher intensity and volume with consistent recovery, enabling this improvement rate to be sustainable over longer blocks of training.",
    [ImprovementRate.Aggressive1]: "An aggressive improvement rate can offer an opportunity to generate a great increase in your training load and fitness levels for a 3-4 week block. When training is executed precisely, it should be followed by a rest week to regain sufficient freshness before starting the next block of hard training.",
    [ImprovementRate.Aggressive2]: "An aggressive improvement rate can offer an opportunity to generate a great increase in your training load and fitness levels for a 3-4 week block. When training is executed precisely, it should be followed by a rest week to regain sufficient freshness before starting the next block of hard training.",
    [ImprovementRate.Extreme2]: "Extreme levels offer an opportunity for high overloading and are great as a short term boost before your taper. Requiring both an increase in time and intensity, use these sparingly throughout the training calendar as they quickly lead to fatigue, requiring additional recovery.",
    [ImprovementRate.Extreme1]: "Extreme levels offer an opportunity for high overloading and are great as a short term boost before your taper. Requiring both an increase in time and intensity, use these sparingly throughout the training calendar as they quickly lead to fatigue, requiring additional recovery.",
}

export type WithRequired<T, K extends keyof T> = T & Required<Pick<T, K>>

export const workoutPlayerTypeToName: {[key in WorkoutPlayerType]: string} = {
  garmin_connect: 'Garmin Connect',
  zwift: 'Zwift',
  xert_ebc: 'Xert EBC',
  wahoo: 'Wahoo',
  mywhoosh: 'MyWhoosh',
  rouvy: 'Rouvy',
  garmin_workout_player: 'Workout Player for Garmin',
  rungap: 'RunGap',
  ictrainer: 'iCtrainer',
};

export const allWorkoutPlayers: WorkoutPlayerType[] = [
  'xert_ebc', 'garmin_connect', 'zwift', 'wahoo', 'mywhoosh', 'rouvy', 'garmin_workout_player', 'rungap', 'ictrainer',
];

export const workoutPlayersThatWeSendWorkoutTo: WorkoutPlayerType[] = [
  'garmin_connect', 'zwift', 'wahoo',
]

export const workoutPlayersThatCallOurAPI: WorkoutPlayerType[] = [
  'xert_ebc', 'mywhoosh', 'rouvy', 'garmin_workout_player', 'rungap', 'ictrainer',
]

// Players that can switch between ERG and slope mode within a workout. Every other player is sent
// mixed mode workouts with slope intervals as free ride or as power targets (see SlopeIntervals).
export const workoutPlayersThatSupportMixedMode: WorkoutPlayerType[] = [
  'xert_ebc', 'garmin_workout_player',
]

export type GroupSharingOptions = "Squad" | "Public" | "Group" | "Private"

export type ReportKeys = 'wr'|'ar'|'br'|'xfair'|'se'
export const reportNames: Record<ReportKeys , string> = {
    'wr': "Workout",
    'ar': "Activity",
    'br': "Breakthrough",
    'xfair': "Forecast",
    'se': "Session",
}

export type SharedActivityRecord = {
    alt: number,
    cad: number,
    factor: number,
    grade: number,
    lat: number,
    lng: number,
    mm: number,
    mmpMid: number,
    mpa: number,
    power: number,
    rp: number,
    spd: number,
    unix_time: number,
    moving: boolean,
}

export type SharedActivityOutput = {
    metrics?: {
        ADU: number,
        AP: number,
        INFRONT: 0,
        MEP: number,
        MES: number,
        TW: number,
        avg_rp: number,
    },
    records?: SharedActivityRecord[],
}

export type SharedActivityDetails = {
    name: string,
    path: string,
    group_sharing: string,
    matched: boolean,
    range_score: number,
    user: {
        me?: boolean,
        avatar: string;
        club: string,
        display_name: string,
        first_name: string,
        last_name: string,
        sig: Signature,
        username: string,
        weight: number,
    }  
}

export type SharedActivity = SharedActivityDetails & SharedActivityOutput
export type SharedActivityMarked = SharedActivity & {
    dirty: boolean,
}
export type XAxisData = {unix_time: number, mm: number}
export type LatLngData = XAxisData & {lat: number, lng: number}

export type ActivityAxisType = "distance" |  "time"

export type SeriesVisibilityType = "startVisible" | "off" | "startHidden"
export type ActivityChartSeriesVisibility = {
    power: SeriesVisibilityType,
    mpa: SeriesVisibilityType,
    cadence: SeriesVisibilityType,
    difficulty: SeriesVisibilityType,
    altitude: SeriesVisibilityType,
    speed: SeriesVisibilityType,
    heartRate: SeriesVisibilityType,
    targetPower: SeriesVisibilityType,
}
export const defaultActivityChartSeriesVisibility: ActivityChartSeriesVisibility = {
    power: "startVisible",
    mpa: "startVisible",
    cadence: "startVisible",
    difficulty: "startVisible",
    altitude: "startVisible",
    speed: "startVisible",
    heartRate: "startVisible",
    targetPower: "startVisible",
}

export type SeriesVisibility = Partial<typeof defaultActivityChartSeriesVisibility>

export type MagicBucketsRecord = {
    dist: number,
    mm: number,
    mmp0s: number,
    mmpMid: number,
    mpa: number,
    p_raw: number,
    power: number,
    timestamp: number,
    unix_time: number,
    xssr: number,
}

type IntervalDurationType = {
    type: "absolute" | "relative",
    value: string,
}

type IntervalPowerType = {
    type: "relative_ftp" | "absolute",
    value: number,
}

type IntervalRangeType = {
    duration: number,
    start: number,
    end: number,
    rib_duration: number,
}

export type IntervalData = {
    duration: IntervalDurationType,
    interval_count: number,
    name: string,
    power: IntervalPowerType,
    range: IntervalRangeType,
    rib_duration: IntervalDurationType,
    rib_power: IntervalPowerType,
    sequence: number,
}

export type MagicBucketsSummary = {
    baseIntervalDuration: number,
    cl: number,
    duration: number,
    intervalDuration: number,
    intervalPower: number,
    numIntervals: number,
    recoveryPower: number,
    xds: number,
    xhss: number,
    xlss: number,
    xpss: number,
    xss: number,

}

export type MagicBucketsData = {
    records: MagicBucketsRecord[],
    intervals: IntervalData[],
    summary: MagicBucketsSummary,
    signature: Signature,
}

export type MagicBucketsType = "workout" | "autogen" | "manual"

export interface XertSegment {
    achievements: any[],
    activity: { id: number, visibility: string, resource_state: number },
    athlete: { id: number, resource_state: number },
    average_cadence: number,
    average_watts: number,
    device_watts: boolean,
    distance: number,
    elapsed_time: number,
    end_index: number,
    hidden: boolean,
    id: string,
    kom_rank: null,
    moving_time: number,
    name: string,
    pr_rank: null,
    resource_state: number,
    segment: {
        activity_type: string,
        average_grade: number,
        city: string,
        climb_category: number,
        country: string,
        distance: number,
        elevation_high: number,
        elevation_low: number,
        elevation_profile: null,
        elevation_profiles: null,
        end_latlng: [number, number],
        hazardous: boolean,
        id: number,
        maximum_grade: number,
        name: string,
        private: boolean,
        resource_state: number,
        starred: boolean,
        start_latlng: [number, number],
        state: string,
    },
    start_date: string,
    start_date_local: string,
    start_index: number,
    start_time: number,
    visibility: string,
}

interface EffortStream {
    data: number[], 
    series_type: string, 
    original_size: number, 
    resolution: string,
}

interface EffortStreams {
    altitude: EffortStream,
    distance: EffortStream,
    latlng: EffortStream,
    time: EffortStream,
    watts: EffortStream,
}

type StravaSegmentRecord = {
    alt: number,
    bt_overage_power: number | null,
    cad: number | null,
    dist: number,
    hr: number | null,
    hws: number,
    index: number,
    lat: number,
    lng: number,
    lws: number,
    min_overage_power: number | null,
    mm: number,
    mmp0s: number,
    mmpMid: number,
    mpa: number,
    mpaColor: string,
    p_raw: number,
    power: number,
    powerColor: string,
    proximity: number,
    pws: number,
    realPower: number,
    spd: number | null,
    t_scaled: number,
    tgc: number,
    tgf: number,
    tgt: number | null,
    time: number,
    tws: number,
    unix_time: number,
    wexp: number,
    xds: number,
    xssr: number,
}

interface StravaLocalLegend {
    athlete_id: number, 
    destination: string,
    effort_count: string, 
    effort_description: string, 
    effort_counts:{overall: string, female: string}
    profile: string,
    title: string,
}

export interface StravaSegmentInfo {
    activity_type: string,
    athlete_count: number,
    athlete_segment_stats: { 
        effort_count: number,
        pr_activity_id: number,
        pr_activity_visibility: string,
        pr_date: string,
        pr_elapsed_time: number,
        pr_visibility: string,
    },
    average_grade: number,
    city: string,
    climb_category: number,
    country: string,
    created_at: string,
    distance: number,
    effort_count: number,
    effort_streams: EffortStreams,
    elevation_high: number,
    elevation_low: number,
    elevation_profile: string,
    elevation_profiles: { dark_url: string, light_url: string },
    end_latlng: [number, number],
    estimate_current: { duration: number, scale: number, records: StravaSegmentRecord[], sig: Signature},
    hazardous: boolean,
    id: number,
    local_legend: StravaLocalLegend,
    map: { id: string, polyline: string },
    maximum_grade: number;
    name: string;
    pr_effort: XertSegment & {streams: EffortStreams},
    pr_sig: Signature,
    pr_xert_path: string,
    private: boolean,
    resource_state: number,
    star_count: number,
    starred: boolean,
    start_latlng: [number, number],
    state: string,
    streams: EffortStream & {type: string},
    total_elevation_gain: number,
    updated_at: string,
    xom: string,
    xom_type: string,
    xoms: {
        destination: {
            href: string,
            name: string,
            type: string,
        },
        kom: string,
        overall: string,
        qom: string,
    },
}

export type AfterChartZoom = (startIndex: number, endIndex: number, data: XAxisData[], axisType: ActivityAxisType) => void

export type PowerType = "watts" | "relative" |"wattsKg"

// Google Maps Icons
export const iconDiamond = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABIAAAAQCAMAAAAs2N9uAAAA8FBMVEUAAACB/1CB/1GE/1aH/1mL/12M/2KO/2OP/2SR/2eR/2iE/1SM/2KR/2iE/1md/3eD/1GM/2CY/3OO/2KS/2mJ/12R/2iV/2+i/3+V/22a/3SN/2CW/3Ck/4Gk/4OX/2+c/3aM/2CZ/3WI/1uS/2mT/2qO/2SK/12W/3CC/1OJ/1t6/0eG/1iZ/3OZ/3KJ/1qR/2lE/wBF/wBE/wBF/wBG/wBH/wBI/wBJ/wJK/wRN/whO/wlQ/wtS/w5T/xFU/xJV/xRX/xZY/xdZ/xle/x9f/yJi/yVi/ydj/ydl/ypm/ytn/yxo/y5p/zBz/z3///9OG8YzAAAAM3RSTlMAAAAAAAAAAAAAAAEBAQYGHSIiKCgqKk9PUFBeXmBgbm6AgJaWm56jo6SkwcHHyN/f/v7XiytBAAAAm0lEQVR4AVXBBXaDQBiAwY/SLnV3d5dCgB/iLkTuf5zwNguPzKA5HM6mB9gUnA88r3tGboPjiaRGR2xi3IxdSbnJLQurF20xOpfrgMVVPxAjSK5R8FANJRfWH+GkJ0uGp+x9B1JQ+tpF8R9LLvrDgm0+K2KUP9hC+/FF838xdnhtSKr5sk9G8RSJxM+wRsbmrdZ6x6Fghbt7FNocqOMhODE9zK8AAAAASUVORK5CYII="
export const iconDiamondOrange = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABIAAAAQCAYAAAAbBi9cAAABz0lEQVQ4T2NkwAP+H65QYrh99C7Dr88MDDKWyow+0+/hUs6IS+L/oUZ/hovLNjC8vfkfrIZfnpFBJzSA0bVnIzY9WA36vzNHg+HeoesMLy+h6hHVYmBQsdFkdJ91A90wDIP+H2pKYLiyaj7Dqysgl6DL/2cQ1WZkMIhJZLSuXIBsGIrC/zuLAxguLVnP8PUlvqBjYOCRZGAwSAhkdGnfAFMIN+j/5pQ4hvsHFzK8u43fEJisqDYDg5pXPKNr9yKQENig/xtTGhiuLK9n+P2VOENgqjgEGRiM0xsZXTsaGP9vSNRieHjwKsN7nDGL33BhdQYGZTdtxv9XV7Ex3Niwm+HyMjvSnANVreJ+iMEw2RURRlsy7zOcm63A8O8PceYxMjEw6EQ8YAxepogIo///GYHg//91sUsYrq2JZvjzHb9hLBwMDOp+SxlDV8X8h+rFTEc7i48zHO+1wGuSVclJRrceFDWo6Qjmsk0p8xiur0tk+P4O1TwuEQYG3cg5DD+/NDEGzH+MM0EiS/xfHzcNmDgzGf7/gwgzsTAwGKVOB2bcLKLzGkzh//XxixlurI9hAIWJXswSRve+WFxexpn74YZtz89jYGVnYHTpmoQv3ABbjpbhdAFWugAAAABJRU5ErkJggg=="
export const iconBicycle = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAMAAADXqc3KAAABEVBMVEUAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABoX7lMAAAAWnRSTlMAAQIDBAUHCQsMDQ8QERITFBUWGBweIiYqKzI0Njc6O0BBQ0VMUFZdXmJjZGdoaWttcHF0gIOFj5GUnaCjqquvsrW3uby+wMjMzs/R09XX2tzg4ubx8/X5+/1tBM6+AAAA4klEQVQYGc3BizrCcBjA4d+/tXUip0pSTqUk52MKCUNmRmZr3/1fiC2Px9MV8L78E6pWry8ySaVC8fPOZZBhwq6ECB2aTNKT72KGBrLJr2Quw8HHXsT1RulELkvEuBYRR/KEysF82xMRpwBx52qKBQlmgWl/TX+TI4yWFGgNFPS6+4+gvZ5wbJZGadh2sDdgRyqrspzvPijcEmf3YAifSzCUsWECJTm0pyRK6DeJzIlOxK4RmRHKfhZQNxeMVd0UEOt34NRvFtetZ51vba9RrNqWDlRuvZdGjB8rd561pfGnvgDw7yV91SQnswAAAABJRU5ErkJggg=="
export const iconRunner = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAABGdBTUEAALGPC/xhBQAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAACXBIWXMAAAsTAAALEwEAmpwYAAABWWlUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iWE1QIENvcmUgNS40LjAiPgogICA8cmRmOlJERiB4bWxuczpyZGY9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkvMDIvMjItcmRmLXN5bnRheC1ucyMiPgogICAgICA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0iIgogICAgICAgICAgICB4bWxuczp0aWZmPSJodHRwOi8vbnMuYWRvYmUuY29tL3RpZmYvMS4wLyI+CiAgICAgICAgIDx0aWZmOk9yaWVudGF0aW9uPjE8L3RpZmY6T3JpZW50YXRpb24+CiAgICAgIDwvcmRmOkRlc2NyaXB0aW9uPgogICA8L3JkZjpSREY+CjwveDp4bXBtZXRhPgpMwidZAAAChUlEQVQ4EWVTz2sTQRSeH7tJpA0LLYmIEdokUKR60BSkgpBeRCqFHoxH9eC1/4Ae0osHLz0I4lXwZoJY8GBBISFgemiP6aFKKCqWGAylaX7t7sz6vU03tnXgMTPvfe/t9743y1dXV0WtVuPszKrX62J7e9uZmJi4dHBw8FRKvuk46nU2mzVisZgXwCUuotls8m63e8rgk61WS9l2f93z2D2tveXx8Uh5d/db3bbtkGEY3tjYGBNBpWCn6kgWltWVQx//FcSU4jadw+HwiIERBGmn5J2dHYmWCGjH4/HzoP+OMZcbhvmm1+t9mZ9PnNvf73jAaso5xSBIjkajM5zzCpj8cBynCPqLSqmrlFCt/uzt7e316WP/FQiqHh0drSN4DdYgEJaFAs9B/W4oFLqP/tdINwqMWkBVgyqbprniuu4MgIsQ6yWBsEiHJu4f/BtjpXa7zYnFqAVS9TjYllLm0QLpME0+IXg5lUrdgP9JJBK56XnewtLSkiIWPJfLSZo5+iZhvEKhoCgJ4Bda6xU6g82dwWCwQWdamUzGDPAGVdna2nLxRW9qaiqCoEgmk7pYLN4awhk7FnBjdnZ2HD4byf4EKO4zoCKVSvmz1mwOvj8wFzYtBPsIXxzn60QdY6ym0+lwIpHwWY40wPNERf4VwO+wQxhpszk5GXuE1/YAZ9bv95/RfvIR0d0Xkf4FpfRjiHMZdiWfzyexzzcajd8YaQ1M3gO7YFlWih4ZGP8Tn6pQC0QNAL+3UqnkoV9jbu4ijbkrpflWa2cZ/8tt3F91Oh0RvJlRJeqLlCWjokPABYfO+CM/0a61+5D2EyMfPiQSgwJn17HfxG/dNE2BkcpDpRxGTIOcvyYJP7Cf9Ij0AAAAAElFTkSuQmCC"
export const iconStartMarker = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABIAAAASCAYAAABWzo5XAAAACXBIWXMAAAliAAAJYgFi28+MAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAFySURBVHgBtZS/S8NQEMcvDgrWQhYHneLQyaUUd+ssCi5ddRHdrH9Bkt3BUXAQN3EV58RZkQ4KgkOc6lChwbaCLs/7pu/wNVRjWvqF496De5/36+6IUlJK2WwuW8DWVn219XyHzaEscdCBLO59vqvo7Uk9NG8TMxQBmF5rGRCXnffx1aHL+xMKnq8IY1PV0ibVKns0P7eIqWdZlj8A0juctbpNcq93Cf43AeKvnwrskGHHCUjfOeDFThbEhB1tXdDsdDHm6RLD4imcmM3Bdf4DgRCHeJbNVscAoG0MQn6TPAp/3nBVQOXH1zvKqx5DWp3kBmUB2TSiuv0T2QKKCzNFGkOxgBr4Bf4ByqvlhRW4hoBuCgypljbyMJLk1DoXEBIqrlX2JckyhThkOOuFLUxASCb2Pk5lZOyfECPO5/WADdSax86VZEvnFd5wja+Pk+v3BMQbuptuE5GUuVQ+ugC6gdFS6pQl1J4GBkP6kYd+RZPUN0d3AFbuEWh+AAAAAElFTkSuQmCC"
export const iconEndMarker = "data:image/png;base64,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"

export type TodaysActivities = {
  activities: Exercise[],
  update: (updater: (value: Exercise[]) => Exercise[] ) => void,
}

/** d_constraint above this means a day breaks a constraint. The optimizer leaves values around 1e-6 (rounding) on days that don't, and a recalculation of the saved plan reports those as 0. */
export const CONSTRAINT_EPSILON = 1e-3;

/** Help text for the "Schedule Workouts" option when saving a Forecast AI plan (see TrainingForecastJob::assignWorkoutsToDays()). */
export const SCHEDULE_WORKOUTS_HELP = "When checked, each training day of your forecast gets a workout. Xert picks one from your recommended training that matches the day's targets, length and difficulty limit, or generates one when none fits. When unchecked, training days are saved as plain activities and you can choose workouts later.<br><br><strong>Days with 2 hours or more available:</strong> a manual Magic Buckets activity with that day's XSS targets is one of the choices, picked at random among the workouts that fit, and the day gets one when no workout fits. You can ride it freely or generate a workout for it when the day comes."
